import { Dispatch, SetStateAction } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ContentType } from '@atlassian/learning-components';

import { useAtlassianUser } from 'src/hooks/useAtlassianUser';
import { NotificationType } from 'src/services/http/notifications';
import { GeneratePdfContentType, MyLearningLoaderProps } from 'src/pages/MyLearningPage/types';
import { generatePdf } from 'src/pages/MyLearningPage/children/utils/generatePdf';
import { getCoursesAndLearningPathsTitleAndSlugs, MyLearningResourceData } from 'src/pages/MyLearningPage';
import {
  getFullTranscriptReportData,
  getMyLearning,
  saveOrUpdateContentSavedStatus,
} from 'src/services/http/myLearning';
import { generateCSV } from 'src/pages/MyLearningPage/children/utils/generateCSV';
import { getCourseUrl, getLessonUrl, getPathUrl } from 'src/utils/getUrl';
import { sendUIEvent } from 'src/utils/analytics';

import { CONTENT_TYPE_VALUES, QUERY_PARAM_KEYS, ROUTES } from './constants';
import { FlattenedItem } from './useMyLearningData';

interface UseMyLearningCardActionsParams {
  learningData: MyLearningResourceData | null;
  setLearningData: Dispatch<SetStateAction<MyLearningResourceData | null>>;
  loaderData: MyLearningLoaderProps;
  setIsFullTranscriptDataLoading: Dispatch<SetStateAction<boolean>>;
}

interface UseMyLearningCardActionsReturn {
  handleTitleClick: (item: FlattenedItem) => void;
  handleViewUpdates: (item: FlattenedItem, notificationType: NotificationType) => void;
  handleGeneratePdf: (item: FlattenedItem) => Promise<void>;
  handleSave: (
    contentfulId: string,
    contentType: ContentType.LEARNING_PATH | ContentType.COURSE,
    currentIsSaved: boolean,
    learningPathSlug?: string,
  ) => Promise<void>;
  handleLearningHistoryClick: () => Promise<void>;
  handleBrowseCatalogClick: () => void;
}

export const useMyLearningCardActions = ({
  learningData,
  setLearningData,
  loaderData,
  setIsFullTranscriptDataLoading,
}: UseMyLearningCardActionsParams): UseMyLearningCardActionsReturn => {
  // Use hooks directly
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAtlassianUser();

  // Handle save/unsave content
  const handleSave = async (
    contentfulId: string,
    contentType: ContentType.LEARNING_PATH | ContentType.COURSE,
    currentIsSaved: boolean,
    learningPathSlug?: string,
  ): Promise<void> => {
    // Toggle the saved status - pass false to unsave, true to save
    const isSaved = await saveOrUpdateContentSavedStatus(contentfulId, contentType, !currentIsSaved, learningPathSlug);

    // Update the isSaved flag in place without removing the item
    if (isSaved && learningData) {
      const updatedLearningData: MyLearningResourceData = {
        ...learningData,
        courses:
          learningData.courses?.map((item) =>
            item.contentfulId === contentfulId ? { ...item, isSaved: !currentIsSaved } : item,
          ) || null,
        learningPaths:
          learningData.learningPaths?.map((item) =>
            item.contentfulId === contentfulId ? { ...item, isSaved: !currentIsSaved } : item,
          ) || null,
      };

      setLearningData(updatedLearningData);
    }
  };

  // Handler for learning history download
  const handleLearningHistoryClick = async (): Promise<void> => {
    const myLearningResourceData = loaderData.myLearningResourceData;

    if (!myLearningResourceData) {
      return;
    }
    setIsFullTranscriptDataLoading(true);

    const fullTranscriptData = await getFullTranscriptReportData();
    const inProgressResourceData = await getMyLearning('in-progress');

    const { myLearningResourceData: inProgressTransformedData } = await getCoursesAndLearningPathsTitleAndSlugs(
      null,
      inProgressResourceData,
      null,
    );

    const completedLearningPathData = myLearningResourceData.learningPaths ? myLearningResourceData.learningPaths : [];
    const completedCourseData = myLearningResourceData.courses ? myLearningResourceData.courses : [];
    const inProgressLearningPathsData =
      inProgressTransformedData && inProgressTransformedData.learningPaths
        ? inProgressTransformedData.learningPaths
        : [];
    const inProgressCourseData =
      inProgressTransformedData && inProgressTransformedData.courses ? inProgressTransformedData.courses : [];

    const combinedMyLearningData: MyLearningResourceData = {
      learningPaths: [...completedLearningPathData, ...inProgressLearningPathsData],
      courses: [...completedCourseData, ...inProgressCourseData],
    };

    setIsFullTranscriptDataLoading(false);

    if (!fullTranscriptData) {
      return;
    }

    generateCSV({
      userName: user?.name ?? '',
      fullTranscriptData: fullTranscriptData,
      myLearningData: combinedMyLearningData,
    });
    sendUIEvent('myLearningScreen', 'button', 'clicked', 'downloadCSV', {}, user?.account_id);
  };

  // Navigate to catalog
  const handleBrowseCatalogClick = (): void => {
    navigate(ROUTES.CATALOG);
  };

  // Handle title click with analytics and navigation
  const handleTitleClick = (item: FlattenedItem): void => {
    const isLesson = item.contentType === CONTENT_TYPE_VALUES.LESSON;
    const isCourse = item.contentType === CONTENT_TYPE_VALUES.COURSE;
    const isLearningPath = item.contentType === CONTENT_TYPE_VALUES.LEARNING_PATH;

    // Send analytics event
    if (user && (isCourse || isLearningPath)) {
      sendUIEvent(
        'myLearningScreen',
        'card',
        'clicked',
        'myLearningCard',
        {
          resourceSlug: item.slug,
          type: isCourse ? ContentType.COURSE : ContentType.LEARNING_PATH,
        },
        user.account_id,
      );
    }

    const newSearchParams = new URLSearchParams(searchParams);

    let url = '';

    if (isLesson && item.slug) {
      url = getLessonUrl({
        lessonSlug: item.slug,
        courseSlug: item.courseSlug,
        pathSlug: item.learningPathSlug,
      });
    } else if (isCourse) {
      url = getCourseUrl(item.slug!, item.learningPathSlug, undefined, newSearchParams);
    } else if (isLearningPath) {
      url = getPathUrl(item.slug!, undefined, newSearchParams);
    }

    if (url) {
      navigate(url);
    }
  };

  // Handle viewing updates (opens in new tab with notification type)
  const handleViewUpdates = (item: FlattenedItem, notificationType: NotificationType): void => {
    const isCourse = item.contentType === CONTENT_TYPE_VALUES.COURSE;
    const isLearningPath = item.contentType === CONTENT_TYPE_VALUES.LEARNING_PATH;

    if (!isCourse && !isLearningPath) {
      return;
    }

    const newSearchParams = new URLSearchParams(searchParams);

    newSearchParams.set(QUERY_PARAM_KEYS.NOTIFICATION_TYPE, notificationType);

    let url = '';

    if (isCourse) {
      url = getCourseUrl(item.slug!, item.learningPathSlug, undefined, newSearchParams);
    } else if (isLearningPath) {
      url = getPathUrl(item.slug!, undefined, newSearchParams);
    }

    if (url) {
      window.open(`${ROUTES.LEARNING_PREFIX}${url}`, '_blank');
    }
  };

  // Handle PDF generation
  const handleGeneratePdf = (item: FlattenedItem): Promise<void> => {
    const isCourse = item.contentType === CONTENT_TYPE_VALUES.COURSE;
    const isLearningPath = item.contentType === CONTENT_TYPE_VALUES.LEARNING_PATH;

    if (user) {
      sendUIEvent(
        'myLearningScreen',
        'button',
        'clicked',
        'downloadPDF',
        {
          resourceSlug: item.title,
          resourceType: isLearningPath ? GeneratePdfContentType.LEARNING_PATH : GeneratePdfContentType.COURSE,
        },
        user.account_id,
      );
    }

    return generatePdf({
      title: item.title,
      completionDate: item.completedDate ?? '',
      userName: user?.name ?? '',
      imageUrl: isCourse ? item.badgeUrl : undefined,
      contentType: isLearningPath ? GeneratePdfContentType.LEARNING_PATH : GeneratePdfContentType.COURSE,
    });
  };

  return {
    handleTitleClick,
    handleViewUpdates,
    handleGeneratePdf,
    handleSave,
    handleLearningHistoryClick,
    handleBrowseCatalogClick,
  };
};
